import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - only redirect on actual auth failures, not network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only clear auth and redirect if it's a genuine 401 from the server
    // and not a network error or cancelled request
    if (error.response?.status === 401 && error.response?.data?.detail) {
      // Auth failed - clearing session
      localStorage.removeItem('token');
      localStorage.removeItem('auth-storage');
      // Use replace to prevent back navigation issues
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  loginWithPin: (data) => api.post('/auth/login/pin', data),
  setPin: (data) => api.post('/auth/set-pin', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Restaurant
export const restaurantAPI = {
  get: () => api.get('/restaurant'),
  update: (data) => api.patch('/restaurant', data),
};

// Tables
export const tablesAPI = {
  list: () => api.get('/tables'),
  create: (data) => api.post('/tables', data),
  update: (id, data) => api.patch(`/tables/${id}`, data),
  delete: (id) => api.delete(`/tables/${id}`),
};

// Zones/Sections
export const zonesAPI = {
  list: () => api.get('/zones'),
  getAll: () => api.get('/zones'),
  create: (data) => api.post('/zones', data),
  update: (id, data) => api.patch(`/zones/${id}`, data),
  delete: (id) => api.delete(`/zones/${id}`),
};

// Furniture
export const furnitureAPI = {
  list: () => api.get('/furniture'),
  getAll: () => api.get('/furniture'),
  create: (data) => api.post('/furniture', data),
  update: (id, data) => api.patch(`/furniture/${id}`, data),
  delete: (id) => api.delete(`/furniture/${id}`),
};

// Time Slots
export const timeSlotsAPI = {
  list: () => api.get('/time-slots'),
  create: (data) => api.post('/time-slots', data),
  update: (id, data) => api.patch(`/time-slots/${id}`, data),
  delete: (id) => api.delete(`/time-slots/${id}`),
};

// Reservations
export const reservationsAPI = {
  list: (params) => api.get('/reservations', { params }),
  getByDate: (date) => api.get('/reservations', { params: { date } }),
  get: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  update: (id, data) => api.patch(`/reservations/${id}`, data),
  delete: (id) => api.delete(`/reservations/${id}`),
  export: (format = 'csv', params = {}) => 
    api.get(`/reservations/export`, { 
      params: { format, ...params },
      responseType: format === 'pdf' ? 'blob' : 'text'
    }),
};

// Add alias for tables
tablesAPI.getAll = tablesAPI.list;

// Customers
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  // CRM endpoints
  getStats: () => api.get('/customers/stats'),
  addVisit: (id, spend, notes) => api.post(`/customers/${id}/add-visit`, null, { params: { spend_amount: spend, notes } }),
  search: (q) => api.get('/customers/search', { params: { q } }),
  getBirthdays: (days = 30) => api.get('/customers/birthdays', { params: { days } }),
  // Coupon
  sendCoupon: (id, promoCodeId, sendVia = 'email') => 
    api.post(`/customers/${id}/send-coupon`, null, { params: { promo_code_id: promoCodeId, send_via: sendVia } }),
  // Export
  export: (format = 'csv', params = {}) => 
    api.get(`/customers/export`, { 
      params: { format, ...params },
      responseType: format === 'pdf' ? 'blob' : 'text'
    }),
};

// Waitlist
export const waitlistAPI = {
  list: (params) => api.get('/waitlist', { params }),
  add: (data) => api.post('/waitlist', data),
  update: (id, data) => api.patch(`/waitlist/${id}`, data),
  delete: (id) => api.delete(`/waitlist/${id}`),
  notify: (id) => api.post(`/waitlist/${id}/notify`),
  convert: (id, tableIds) => api.post(`/waitlist/${id}/convert`, null, { params: { table_ids: tableIds } }),
  // Public endpoints for QR
  getQRInfo: (restaurantId) => axios.get(`${API_URL}/api/public/waitlist/qr/${restaurantId}`),
  publicRegister: (restaurantId, data) => axios.post(`${API_URL}/api/public/waitlist/register/${restaurantId}`, data),
  checkStatus: (qrCode) => axios.get(`${API_URL}/api/public/waitlist/status/${qrCode}`),
};

// Analytics
export const analyticsAPI = {
  overview: (params) => api.get('/analytics/overview', { params }),
  daily: (date) => api.get('/analytics/daily', { params: { date } }),
};

// Simulation
export const simulationAPI = {
  getFloorState: (date, time) => api.get('/simulation/floor-state', { params: { date, time } }),
  getTimeline: (date) => api.get('/simulation/timeline', { params: { date } }),
};

// Detection (Multi-reservation)
export const detectionAPI = {
  getMultiReservations: (date) => api.get('/detection/multi-reservations', { params: { date } }),
  flagCustomer: (email, phone, reason) => api.post('/detection/flag-customer', null, { 
    params: { customer_email: email, customer_phone: phone, flag_reason: reason } 
  }),
};

// Promo Codes
export const promoCodesAPI = {
  list: () => api.get('/promo-codes'),
  create: (data) => api.post('/promo-codes', data),
  delete: (id) => api.delete(`/promo-codes/${id}`),
  validate: (code, partySize) => api.get(`/promo-codes/validate/${code}`, { params: { party_size: partySize } }),
};

// Marketing Campaigns
export const campaignsAPI = {
  list: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  send: (id) => api.post(`/campaigns/${id}/send`),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

// Events
export const eventsAPI = {
  list: (status) => api.get('/events', { params: { status } }),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.patch(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  publish: (id) => api.post(`/events/${id}/publish`),
  getStats: (id) => api.get(`/events/${id}/stats`),
  // Tickets
  getTickets: (eventId) => api.get(`/events/${eventId}/tickets`),
  createTicket: (eventId, data) => api.post(`/events/${eventId}/tickets`, data),
  checkIn: (qrCode) => api.post(`/events/tickets/${qrCode}/check-in`),
  searchTickets: (q, eventId) => api.get('/events/tickets/search', { params: { q, event_id: eventId } }),
  // Public
  getPublicEvent: (slug) => axios.get(`${API_URL}/api/public/events/${slug}`),
  publicPurchase: (eventId, data) => axios.post(`${API_URL}/api/public/events/${eventId}/purchase`, data),
};

// Export
export const exportAPI = {
  reservations: (format, dateFrom, dateTo) => api.get('/export/reservations', { 
    params: { format, date_from: dateFrom, date_to: dateTo },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
  customers: (format) => api.get('/export/customers', { 
    params: { format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
  eventTickets: (eventId, format) => api.get(`/export/events/${eventId}/tickets`, { 
    params: { format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
};

// Widget (Public)
export const widgetAPI = {
  availability: (restaurantId, date, partySize) => 
    api.get(`/widget/availability/${restaurantId}`, { params: { date, party_size: partySize } }),
  createReservation: (restaurantId, data) => 
    api.post(`/widget/reservation/${restaurantId}`, data),
};

export default api;
